/**
 * RecommendationsService
 * Calls Recomdo's personalized suggestion endpoint and returns recommendations.
 */
import axios from 'axios';

const SUGGEST_URL =
    (typeof process !== 'undefined' && process.env && process.env.SUGGEST_URL)
    || (typeof window !== 'undefined' && window.__VOICE_SEARCH_ENV_SETTINGS__?.SUGGEST_URL)
    || 'https://pixel.recomdo.ai/api/recommendation/suggest';

function isBrowser() {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function getCookie(name) {
    try {
        if (!isBrowser()) return null;
        return document.cookie
            .split(';')
            .map(s => s.trim())
            .find(s => s.startsWith(name + '='))
            ?.split('=')[1] || null;
    } catch (_) {
        return null;
    }
}

/**
 * Resolve reco_uid from cookie or storage, as per spec.
 * - Cookie name may be 'reco_uid'.
 * - Storage key is 'RECOMDO_UID'.
 */
export function resolveRecoUid() {
    try {
        if (!isBrowser()) return null;
        // Explicit window var if present
        if (typeof window.__RECOMDO_UID__ === 'string') return window.__RECOMDO_UID__;
        const ls = typeof localStorage !== 'undefined' ? localStorage.getItem('RECOMDO_UID') : null;
        if (ls) return ls;
        const ss = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('RECOMDO_UID') : null;
        if (ss) return ss;
        // Cookie fallback
        const cookieVal = getCookie('reco_uid') || getCookie('RECOMDO_UID');
        return cookieVal || null;
    } catch (_) {
        return null;
    }
}

/**
 * Resolve email_sha256 from storage key 'RECOMDO_EMAIL_SHA256_CACHE'.
 */
export function resolveEmailSha256() {
    try {
        if (!isBrowser()) return null;
        const ls = typeof localStorage !== 'undefined' ? localStorage.getItem('RECOMDO_EMAIL_SHA256_CACHE') : null;
        if (ls) return ls;
        const ss = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('RECOMDO_EMAIL_SHA256_CACHE') : null;
        if (ss) return ss;
        // Optional window global
        if (typeof window.__RECOMDO_EMAIL_SHA256__ === 'string') return window.__RECOMDO_EMAIL_SHA256__;
        return null;
    } catch (_) {
        return null;
    }
}

/**
 * Call the suggestion endpoint.
 * @param {string|null} emailSha256
 * @param {string|null} recoUid
 * @returns {Promise<{recommendation_items: Array, timestamp?: string, person_id?: string, profile?: any, context?: any} | null>}
 */
export async function suggest(emailSha256 = null, recoUid = null) {
    try {
        const email_sha256 = emailSha256 ?? resolveEmailSha256();
        const reco_uid = recoUid ?? resolveRecoUid();

        if (!email_sha256 && !reco_uid) {
            return {recommendation_items: []};
        }

        // Cache for ~40 minutes in sessionStorage
        const CACHE_TTL_MS = 40 * 60 * 1000;
        const cacheKey = (() => {
            try {
                const payload = JSON.stringify({email_sha256: email_sha256 || null, reco_uid: reco_uid || null});
                if (typeof btoa === 'function') return 'RECO_SUGGEST_CACHE_' + btoa(payload);
                return 'RECO_SUGGEST_CACHE_' + encodeURIComponent(payload);
            } catch (_) {
                return 'RECO_SUGGEST_CACHE_DEFAULT';
            }
        })();
        try {
            if (typeof window !== 'undefined' && window.sessionStorage) {
                const raw = sessionStorage.getItem(cacheKey);
                if (raw) {
                    const {data, ts} = JSON.parse(raw);
                    if (typeof ts === 'number' && (Date.now() - ts) < CACHE_TTL_MS && data && Array.isArray(data.recommendation_items)) {
                        return data;
                    }
                }
            }
        } catch (_) {
        }

        const response = await axios.post(
            SUGGEST_URL,
            {email_sha256, reco_uid},
            {headers: {'accept': 'application/json', 'Content-Type': 'application/json'}, timeout: 3500}
        );

        if (response && response.status >= 200 && response.status < 300) {
            const data = response.data || {};
            const out = Array.isArray(data.recommendation_items) ? data : {recommendation_items: []};
            try {
                if (typeof window !== 'undefined' && window.sessionStorage) {
                    sessionStorage.setItem(cacheKey, JSON.stringify({data: out, ts: Date.now()}));
                }
            } catch (_) {
            }
            return out;
        }
        return {recommendation_items: []};
    } catch (e) {
        if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
            console.warn('suggest() failed:', e?.message || e);
        }
        return {recommendation_items: []};
    }
}
